import { Text } from "@react-three/drei";
import { useLoader } from "@react-three/fiber";
import type { ThreeSidePathsType } from "@tosize/shapes/src/types/three";
import { formatNumber, getLocaleAsset } from "@tosize/ui";
import { useMemo, useState } from "react";
import {
	CylinderGeometry,
	DoubleSide,
	ExtrudeGeometry,
	MathUtils,
	MeshBasicMaterial,
	RepeatWrapping,
	Shape,
	TextureLoader,
} from "three";
import { usePanelVisualisation } from "../../Context";
import { getPoints } from "../lines/utils";

interface FinishProps {
	side: ThreeSidePathsType;
	minLength: number;
}

export function Finish({ minLength, side }: FinishProps) {
	const { lang, workpiece, thickness } = usePanelVisualisation();
	const { middlePoint } = getPoints(side.path);
	const [active, setActive] = useState(false);

	const upperCircleDiameter = Math.min(0.075, minLength * 0.5);
	const offset = upperCircleDiameter * 0.3;
	const shapeHeight = 0.005;
	const lowerShapeHeight = upperCircleDiameter * 2 + offset;

	const lowerShapeWidth = useMemo(
		() => (active ? lowerShapeHeight * 3 : lowerShapeHeight),
		[active, lowerShapeHeight],
	);

	const circle = useMemo(
		() => new CylinderGeometry(upperCircleDiameter, upperCircleDiameter, shapeHeight, 250),
		[upperCircleDiameter],
	);

	const finishesForSide = useMemo(
		() => Object.entries(workpiece.finishes.side).filter(([key]) => side.groupCodes?.includes(key)),
		[side.groupCodes, workpiece.finishes.side],
	);

	const finishCode = useMemo(
		() =>
			finishesForSide
				.flatMap(([, value]) => Object.keys(value).map((key) => key.split("_").at(0)))
				.filter((value) => value !== "boorgat")
				.at(0),
		[finishesForSide],
	);

	const finishOption = useMemo(
		() => finishesForSide.flatMap(([, value]) => Object.values(value).flat()).at(0),
		[finishesForSide],
	);

	const file =
		finishCode && finishCode !== "boorgat" && finishOption
			? getLocaleAsset(`finishes/${finishCode}/visualisation/${finishOption.option}.png`)
			: getLocaleAsset("finishes/none.png");

	const texture = useLoader(TextureLoader, file);
	texture.wrapS = RepeatWrapping;

	const materials = useMemo(
		() => [
			new MeshBasicMaterial({}),
			new MeshBasicMaterial({ map: texture, color: "#ffffff" }),
			new MeshBasicMaterial({}),
		],
		[texture],
	);

	const biggerCircle = useMemo(() => {
		const roundedShape = new Shape();

		roundedShape.moveTo(0, 0);

		if (lowerShapeHeight !== lowerShapeWidth) {
			roundedShape.lineTo(0, 0);
		}

		roundedShape.arc(
			lowerShapeWidth - lowerShapeHeight,
			lowerShapeHeight / 2,
			lowerShapeHeight / 2,
			MathUtils.degToRad(-90),
			MathUtils.degToRad(90),
			false,
		);

		if (lowerShapeHeight !== lowerShapeWidth) {
			roundedShape.lineTo(-offset, lowerShapeHeight);
		}

		roundedShape.arc(
			0,
			-lowerShapeHeight / 2,
			lowerShapeHeight / 2,
			MathUtils.degToRad(90),
			MathUtils.degToRad(-90),
			false,
		);

		const extrudeSettings = {
			steps: 20,
			depth: shapeHeight,
			bevelEnabled: false,
		};

		return new ExtrudeGeometry(roundedShape, extrudeSettings);
	}, [lowerShapeHeight, lowerShapeWidth, offset]);

	const biggerCircleMaterial = useMemo(
		() =>
			new MeshBasicMaterial({
				color: "#ffffff",
				side: DoubleSide,
			}),
		[],
	);

	const label = useMemo(() => {
		return finishOption
			? Object.entries(finishOption.option_parameters).map(([key, value]) =>
					formatNumber({
						lang,
						value: Number(value),
						style: "unit",
						unit: key === "degrees" ? "degree" : "millimeter",
						digits: { min: 0, max: 0 },
					}),
				)
			: undefined;
	}, [finishOption, lang]);

	return (
		<group visible={!!finishCode && !!finishOption} rotation={[Math.PI / 2, 0, 0]}>
			<group
				position={[middlePoint.x - lowerShapeHeight / 2, 0, -middlePoint.y + lowerShapeHeight / 2]}
			>
				<group position={[lowerShapeHeight / 2, thickness, -lowerShapeHeight / 2]}>
					<mesh
						onPointerEnter={() => setActive(true)}
						onPointerLeave={() => setActive(false)}
						geometry={circle}
						castShadow
						material={materials}
						position={[0, 0.003, 0]}
						rotation={[0, Math.PI / 2, 0]}
					/>

					<group rotation={[-Math.PI / 2, 0, 0]} visible={active}>
						<mesh
							receiveShadow
							geometry={biggerCircle}
							material={biggerCircleMaterial}
							position={[offset, -lowerShapeHeight / 2, 0]}
						/>
					</group>

					<Text
						fontSize={upperCircleDiameter}
						color={"#000000"}
						position={[lowerShapeHeight + offset * 3, shapeHeight * 1.25, 0]}
						rotation={[-Math.PI / 2, 0, 0]}
						visible={active}
					>
						{label}
					</Text>
				</group>
			</group>
		</group>
	);
}
